import { useEffect, useMemo, useState } from "react";
import { bangkokForecastTime } from "../lib/time";
import {
  aggregateIncidentFeeds,
  buildActions,
  buildTiles,
  isBriefObservationCurrent,
  type AirBriefInput,
  type BriefAction,
  type BriefTile,
  type FeedHealthInput,
  type IncidentBriefFeed,
  type MayorBriefInputs,
} from "../lib/mayorBrief";
import type { PrecipNowcast, ReservoirObservation } from "@chonburi/shared";
import type { MarineSnapshot } from "../components/CoastalBrief";
import type { TideSnapshot } from "../components/TidePanel";

/**
 * The four conditions, built once for every surface that shows them.
 *
 * The mayor brief and the mayor's desk both answer "what is the state of
 * the town this morning", and they must never disagree about the same dam
 * or the same wave. So the shaping of the raw feeds into the four tiles
 * lives here, and both surfaces call it.
 */

export interface BriefFeedMeta {
  ageMinutes: number | null;
  loadedAt: string | null;
  fallbackTier: string;
  source: string;
}

export interface BriefSurfaceInputs {
  air: AirBriefInput | null;
  rain: PrecipNowcast | null;
  rainMeta: BriefFeedMeta;
  reservoirs: ReservoirObservation[];
  reservoirMeta: BriefFeedMeta;
  marine: MarineSnapshot | null;
  marineMeta: BriefFeedMeta;
  tides: TideSnapshot | null;
  tideMeta: BriefFeedMeta;
  incidentFeeds: IncidentBriefFeed[];
  feeds: FeedHealthInput;
}

export interface BriefSurface {
  tiles: BriefTile[];
  actions: BriefAction[];
  now: Date;
}

/**
 * Build the brief's inputs from the raw feeds.
 *
 * Exported so the shaping can be tested on its own, away from the clock.
 */
export function briefInputs(input: BriefSurfaceInputs, now: Date): MayorBriefInputs {
  // The dam that runs dry first: usable water above dead storage ÷ today's
  // net depletion. Fill percentage is not the signal — a shallow basin sits
  // low all dry season, and levelling on it cries wolf every October.
  const currentReservoirs = input.reservoirs.filter((r) =>
    isBriefObservationCurrent(r.observedAt, now, 36),
  );
  const withHorizon = currentReservoirs.filter(
    (r) => r.daysRemaining != null && Number.isFinite(r.daysRemaining),
  );
  const tightest = withHorizon.length
    ? withHorizon.reduce((lo, r) =>
        (r.daysRemaining ?? Infinity) < (lo.daysRemaining ?? Infinity) ? r : lo,
      )
    : null;
  // Flows that are not published cannot be read as a dam that is fine.
  const flowsUnknown = currentReservoirs.some((r) => r.trend === "unknown");
  // The report's own date, not the moment the dashboard fetched it.
  const storageObservedAt = tightest?.observedAt ?? currentReservoirs[0]?.observedAt ?? null;

  const { rain, rainMeta, reservoirs, reservoirMeta, marine, marineMeta, tides, tideMeta } = input;

  return {
    now,
    air: input.air,
    rain:
      rain && rainMeta.fallbackTier === "live"
        ? {
            nowMm: rain.nowMm,
            total2hMm: rain.total2hMm,
            peakMm: rain.peakMm,
            minutesToSignificant: rain.minutesToSignificant,
            intensity: rain.intensity,
            observedAt: bangkokForecastTime(rain.validAt ?? rain.points?.[0]?.at),
            source: rainMeta.source,
          }
        : null,
    water:
      currentReservoirs.length > 0 && reservoirMeta.fallbackTier === "live"
        ? {
            tightest:
              tightest && tightest.daysRemaining != null
                ? {
                    name: tightest.name,
                    daysRemaining: tightest.daysRemaining,
                    capacityPct: tightest.capacityPct,
                  }
                : null,
            flowsUnknown,
            observedAt: storageObservedAt,
            source: reservoirMeta.source,
          }
        : null,
    sea:
      marine && marineMeta.fallbackTier === "live"
        ? {
            waveHeightM: marine.waveHeightM,
            windKmh: marine.windKmh,
            windGustsKmh: marine.windGustsKmh,
            smallBoatBelowThreshold: marine.smallBoatBelowThreshold,
            ferryBelowThreshold: marine.ferryBelowThreshold,
            peakWaveHeightNext24hM: marine.peakWaveHeightNext24hM,
            tideHeightM:
              tides && isBriefObservationCurrent(tides.observedAt, now, 1) ? tides.heightM : null,
            tideRising:
              tides && isBriefObservationCurrent(tides.observedAt, now, 1) ? tides.rising : null,
            springTide:
              tides && isBriefObservationCurrent(tides.observedAt, now, 1) ? tides.springTide : null,
            observedAt: marine.observedAt,
            source: [marineMeta.source, tideMeta.source].filter(Boolean).join(" · "),
          }
        : null,
    incidents: aggregateIncidentFeeds(input.incidentFeeds, now),
    feeds: input.feeds,
  };
}

/**
 * The tiles, the checks they raise, and the clock they are aged against.
 *
 * The clock ticks every minute so a reference time never drifts while the
 * screen is open.
 */
export function useBriefSurface(input: BriefSurfaceInputs): BriefSurface {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const {
    air, rain, rainMeta, reservoirs, reservoirMeta,
    marine, marineMeta, tides, tideMeta, incidentFeeds, feeds,
  } = input;

  const tiles = useMemo(
    () =>
      buildTiles(
        briefInputs(
          { air, rain, rainMeta, reservoirs, reservoirMeta, marine, marineMeta, tides, tideMeta, incidentFeeds, feeds },
          now,
        ),
      ),
    [
      air, rain, rainMeta, reservoirs, reservoirMeta,
      marine, marineMeta, tides, tideMeta, incidentFeeds, feeds, now,
    ],
  );

  const actions = useMemo(() => buildActions(tiles, feeds), [tiles, feeds]);

  return { tiles, actions, now };
}
